import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { startWith } from 'rxjs';
import { ChassisTimeTableModel } from 'src/app/models/ChassisTimeTable';
import { ChassisService } from 'src/app/services/chassis/chassis.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-chassis-time-table-form',
  templateUrl: './chassis-time-table-form.component.html',
  styleUrl: './chassis-time-table-form.component.css'
})
export class ChassisTimeTableFormComponent {
  form: FormGroup;
  processOptions: string[] = ['RECEBIMENTO', 'EXPEDIÇÃO']

  @Input() receivedTrip!: ChassisTimeTableModel | null;
  @Output() closeForm = new EventEmitter<void>();
  @Output() dataUpdated = new EventEmitter<void>();

  apiUrl: string = environment.apiLink;

  constructor(
    private formBuilder: FormBuilder,
    private chassisService: ChassisService,
  ) {
    this.form = this.formBuilder.group({
      classification: ["time-table"],
      id: [null],
      trip: [null, [Validators.required]],
      process: [null, [Validators.required]],
      arrivalSLA: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
      departureSLA: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
      extra: [null],
    })
  }

  ngOnInit() {
    const currentClassification = this.form.get('classification')?.value
    this.updateValidatorsBasedOnTripType(currentClassification);
    this.tripTypeCheck()
    this.handleTripTypeChange()
  }

  ngOnChanges() {
    if (this.receivedTrip) {
      this.updateFormValues(this.receivedTrip)
      this.checkAndUpdateTripType(this.receivedTrip)
      this.handleTripTypeChange()
    } else this.resetForm()
  }

  private updateFormValues(trip: ChassisTimeTableModel) {
    this.form.patchValue({
      id: trip.id,
      trip: trip.trip,
      process: trip.processDescription,
      arrivalSLA: trip.arrivalTimeSLA,
      departureSLA: trip.departureTimeSLA,
      extra: trip.extra,
    });
  }

  private checkAndUpdateTripType(trip: ChassisTimeTableModel) {
    if (this.isExtra(trip)) {
      this.form.get('classification')?.setValue('extra')
    } else {
      this.form.get('classification')?.setValue('time-table')
    }
  }

  private handleTripTypeChange() {
    const selectedValue = this.form.get('classification')?.value;

    if (selectedValue === 'time-table') {
      this.toggleControls(['extra'], false)
      this.form.controls['extra'].disable()
    } else if (selectedValue === 'extra') {
      this.toggleControls(['arrivalSLA'], false)
      this.toggleControls(['departureSLA'], false)
      this.form.controls['arrivalSLA'].disable()
      this.form.controls['departureSLA'].disable()
    }
  }

  private tripTypeCheck() {
    const classificationControl = this.form.get('classification')
    if (classificationControl) {
      classificationControl.valueChanges.pipe(
        startWith(classificationControl.value)).subscribe(selectedValue => {
          if (selectedValue) {
            this.updateValidatorsBasedOnTripType(selectedValue);
          }
        })
    }
  }

  private isExtra(trip: ChassisTimeTableModel): boolean {
    return trip.extra != null   
  }

  private updateValidatorsBasedOnTripType(tripType: string) {
    if (tripType === 'time-table') {
      this.disableAndClearValidators(['extra'])
      this.enableAndSetValidators('arrivalSLA', [Validators.required])
      this.enableAndSetValidators('departureSLA', [Validators.required])
    } else {
      this.disableAndClearValidators(['arrivalSLA'])
      this.disableAndClearValidators(['departureSLA'])
      this.enableAndSetValidators('extra', [Validators.required])
    }
    this.form.updateValueAndValidity()
  }

  private toggleControls(controlNames: string[], enable: boolean, validators: any[] = []) {
    controlNames.forEach(controlName => {
      const control = this.form.get(controlName)
      if (enable) {
        control?.enable()
        control?.setValidators(validators)
      } else {
        control?.disable()
        control?.clearValidators()
        control?.reset()
      }
      control?.updateValueAndValidity()
    })
  }

  disableAndClearValidators(controlNames: string[]) {
    this.toggleControls(controlNames, false)
  }

  enableAndSetValidators(controlName: string, validators: any[]) {
    this.toggleControls([controlName], true, validators)
  }

  submitForm() {
    const rawData = this.form.getRawValue();
    const classification = rawData.classification;

    if (!rawData.trip) {
      alert("Nome da viagem é obrigatório!")
      return
    }

    const trimmedTripName = rawData.trip.trim();
    this.form.get('trip')?.setValue(trimmedTripName)

    if (classification === "time-table" && (!rawData.arrivalSLA || !rawData.departureSLA)) {
      alert("Preencher campos de horário de entrada e saída ")
      return
    }

    if (classification === "extra" && !rawData.extra) {
      alert("Campo TAKT é obrigatório para viagens extras")
      return
    }

    if (this.form.invalid) {
      alert("Preencha os campos obrigatórios!")
      return
    }

    const newTrip: ChassisTimeTableModel = {
      id: null,
      trip: trimmedTripName,
      processDescription: rawData.process,
      arrivalTimeSLA: rawData.arrivalSLA,
      departureTimeSLA: rawData.departureSLA,
      extra: rawData.extra,
    };

    this.chassisService.createTrip(newTrip).subscribe({
      next: () => {
        alert("Dados inseridos com sucesso");
        this.resetForm();
        this.dataUpdated.emit()
      },
      error: (error) => {
        console.error("Erro ao enviar os dados: ", error);
        alert("Erro ao inserir dados");
      },
    });
  }

  updateTrip() {
    if (this.form.valid) {

      const rawData = this.form.getRawValue();
      const trimmedTripName = rawData.trip?.trim();

      const updatedTrip = {
        id: rawData.id,
        trip: trimmedTripName,
        processDescription: rawData.process,
        arrivalTimeSLA: rawData.arrivalSLA,
        departureTimeSLA: rawData.departureSLA,
        extra: rawData.extra,
      };

      this.chassisService.updateTrip(updatedTrip, rawData.id).subscribe({
        next: () => {
          alert("Viagem atualizada");
          this.resetForm();
          this.dataUpdated.emit()
        },
        error: (error) => {
          console.error("Erro ao atualizar dados: ", error);
          alert("Erro ao atualizar viagem");
        },
      });
    } else {
      alert("Preencha os campos corretamente");
    }
  }

  resetForm() {
    this.form.enable()
    this.form.reset({
      classification: "time-table",
      trip: null,
      process: null,
      arrivalSLA: null,
      departureSLA: null,
      extra: null,
    });
    this.receivedTrip = null
  }

  onClose() {
    this.resetForm()
    this.closeForm.emit()
  }

  isNewTrip(): boolean {
    return this.receivedTrip == null;
  }

  formatTime(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Remove caracteres não numéricos

    if (value.length >= 3) {
      value = value.substring(0, 2) + ':' + value.substring(2, 4); // Adiciona ":"
    }

    input.value = value.substring(0, 5); // Limita ao formato HH:mm
    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.form.controls[controlName].setValue(value);
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation()
  }
}
